import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { BACK_SHARE, cardBackColors, cardBackUrl, cardFaceUrl, HAND_SIZE } from "./cardImages";
import { inStroke, outStroke, STICTION_BREAK_AT, type SlideDirection } from "./slide";
import { buildTransition, CARD_RADIUS, dealRail, FACE_DOWN, type Move, type TransitionCard, type Viewport } from "./layout";
import { useTransition } from "./transitionStore";
import { sound } from "../../audio/sound";

/**
 * The pack of cards that covers the screen while the route changes underneath
 * it.
 *
 * Three things about how it is built are worth knowing.
 *
 * **It is inset to the tray, not to the window.** `#root` is a wooden frame with
 * the room showing round it, and `body::after` paints the rail's inner shading
 * over the content. Covering all of that would mean cards flying over the
 * furniture; inset to `--inner-*` with the tray's own radius, they land ON the
 * table, which is the only reading that makes sense in this app. `.gift-overlay`
 * is inset the same way for the same reason.
 *
 * **The cards are driven by the Web Animations API, not by CSS keyframes.**
 * Every card has its own start, end, delay and duration, and keyframes cannot
 * carry per-element values without registering custom properties. `el.animate()`
 * takes concrete values per element, composites transform and opacity off the
 * main thread exactly as CSS would, and — the part that matters — can be
 * cancelled, which is what stops a reveal fighting a cover that is still
 * running. A browser without it never gets here: the store treats a missing
 * `Element.prototype.animate` as reduced motion.
 *
 * **Nothing animates anything but `transform` and `opacity`.** This screen's
 * cost is fill rate (see `pixi/renderQuality.ts`), and forty cards each with a
 * blurred shadow being re-rasterised per frame is the single most expensive
 * thing this app could put on a phone. The shadow is one static, cheap
 * `box-shadow`; the card face is one shared image.
 */
export function ScreenTransition() {
  const run = useTransition((s) => s.run);
  const phase = useTransition((s) => s.phase);
  const boxRef = useRef<HTMLDivElement>(null);
  const [viewport, setViewport] = useState<{ width: number; height: number } | null>(null);

  // Measured from the overlay's own box rather than the window, so the tray's
  // inset and the safe areas are already accounted for.
  useLayoutEffect(() => {
    if (!run) return;
    const el = boxRef.current;
    if (!el) return;
    const measure = () => setViewport({ width: el.clientWidth, height: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [run?.id]);

  const cards = useMemo(() => {
    if (!run || !viewport || run.plan.style === "fade") return [];
    return buildTransition(run.plan.style, viewport, run.plan, {
      back: run.back,
      seed: run.seed,
      handSize: HAND_SIZE,
      backShare: BACK_SHARE,
    });
  }, [run?.id, run?.plan, run?.back, run?.seed, viewport]);

  useCardAnimations(boxRef, cards, phase, run?.id ?? 0, viewport);
  useSlideAnimation(run?.plan.style === "slide" ? (run?.direction ?? null) : null, phase, run?.id ?? 0, run?.plan ?? null);
  useTransitionSound(run?.id ?? 0, run?.plan.style ?? "fade", phase, run?.plan.revealMs ?? 0, run?.plan.coverMs ?? 0);

  if (!run) return null;

  const back = cardBackUrl();
  const colors = cardBackColors();

  /**
   * The image for one position, falling back twice.
   *
   * A face that has not finished rendering shows the BACK rather than a blank,
   * which is why the back is primed first: a half-dealt hand then reads as a
   * pack with some cards face down, which is what a pack of cards looks like
   * anyway. Only a deck with no images at all reaches the color swatch.
   */
  const imageFor = (art: number): string | null => (art === FACE_DOWN ? back : (cardFaceUrl(art) ?? back));

  return (
    <div
      ref={boxRef}
      className={`tx-overlay tx-${run.plan.style}`}
      data-phase={phase}
      aria-hidden
      // The fade is the one style driven by CSS rather than by `el.animate()`,
      // so its length has to be published: it is one cross-fade, in two halves.
      style={{
        ["--tx-fade-ms" as string]: `${Math.round(run.plan.totalMs / 2)}ms`,
      }}
    >
      {cards.map((c) => {
        const src = imageFor(c.art);
        return (
        <div
          key={c.key}
          className="tx-card"
          style={{
            width: c.width,
            height: c.height,
            zIndex: c.z,
            borderRadius: `${CARD_RADIUS.x}% / ${CARD_RADIUS.y}%`,
            // The deck's own two colors under the art, so a card is in the
            // right deck even on the first transition of a cold session, before
            // `cardImages` has had a chance to render anything.
            background: src ? `center / 100% 100% no-repeat url(${src}), ${colors.primary}` : colors.primary,
            boxShadow: src ? undefined : `inset 0 0 0 2px ${colors.secondary}`,
            // Placed where it LANDS. The animation carries it in from its start
            // and out to its end, so a card with no animation is a card sitting
            // correctly in the covered pack rather than one stuck off-screen.
            transform: transformOf({ x: c.x, y: c.y, rot: c.rot, scale: 1 }),
          }}
        />
        );
      })}
      {/* The brass slider that pushes the deal off. Only the dealt style has one:
          a gust needs no machinery, and the cut is the pack itself moving. */}
      {run.plan.style === "deal" && <div className="tx-rail" />}
    </div>
  );
}

type Placed = Pick<Move, "x" | "y" | "rot" | "scale">;

/** Centered on its own point, so `x`/`y` are the card's middle rather than its corner. */
function transformOf(p: Placed): string {
  return `translate(${p.x}px, ${p.y}px) translate(-50%, -50%) rotate(${p.rot}deg) scale(${p.scale})`;
}

/**
 * Drives every card, and the rail, for the phase the run is in.
 *
 * Existing animations are cancelled before new ones are created. That is not
 * housekeeping: without it a card whose entrance is still running when the
 * reveal begins would have two animations writing its transform, and the one
 * that wins is whichever the browser composites last.
 */
function useCardAnimations(
  boxRef: React.RefObject<HTMLDivElement>,
  cards: TransitionCard[],
  phase: "covering" | "revealing",
  runId: number,
  viewport: Viewport | null,
): void {
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!box || cards.length === 0) return;
    const els = Array.from(box.querySelectorAll<HTMLElement>(".tx-card"));
    const running: Animation[] = [];

    els.forEach((el, i) => {
      const card = cards[i];
      if (!card) return;
      for (const a of el.getAnimations()) a.cancel();
      const landed: Placed = { x: card.x, y: card.y, rot: card.rot, scale: 1 };
      const move = phase === "covering" ? card.enter : card.exit;
      const from = phase === "covering" ? move : landed;
      const to = phase === "covering" ? landed : move;
      const frames = move.path
        ? move.path.map((p) => ({ offset: p.offset, transform: transformOf({ ...p, scale: 1 }) }))
        : [{ transform: transformOf(from) }, { transform: transformOf(to) }];
      running.push(
        el.animate(frames, {
          duration: Math.max(1, move.durMs),
          delay: move.delayMs,
          easing: move.ease,
          // `both`, so a card waiting out its delay is held at its start rather
          // than sitting in the landed pack, and one that has finished stays
          // where it finished instead of snapping back.
          fill: "both",
        }),
      );
    });

    const rail = box.querySelector<HTMLElement>(".tx-rail");
    if (rail && viewport) {
      for (const a of rail.getAnimations()) a.cancel();
      const reveal = cards[0]?.exit;
      // Sampled from the SAME function every pushed card's path was, so the bar
      // and the pile against it cannot drift apart: the cards are not timed to
      // look as if the rail moves them, they are placed wherever its face is.
      // In PIXELS, because a percentage translate resolves against the element's
      // own width rather than the box it has to cross.
      const track = dealRail(cards, viewport);
      rail.style.width = `${track.width}px`;
      const at = (face: number) => ({ transform: `translateX(${face - track.width}px)` });
      if (phase === "revealing" && reveal) {
        const frames = track.knots.map((u) => ({ offset: u, ...at(track.face(u)) }));
        running.push(
          rail.animate(frames, {
            duration: Math.max(1, reveal.durMs + reveal.delayMs),
            easing: "linear",
            fill: "both",
          }),
        );
      } else {
        rail.animate([at(track.from)], { duration: 1, fill: "both" });
      }
    }

    return () => {
      for (const a of running) a.cancel();
    };
  }, [runId, phase, cards, viewport]);
}

/**
 * Slides the screen itself, rather than anything in the overlay.
 *
 * The element is the stage's only child — whatever route is mounted — found
 * through `.tx-stage`, which generates no box of its own precisely so that
 * taking hold of the screen does not change how the screen is laid out.
 *
 * Two things here are the difference between a slide and a wipe.
 *
 * **The transform is cleared when the run ends, not left filled.** A transformed
 * element is the containing block for any `position: fixed` inside it, so a
 * screen left holding `translateY(0)` would quietly re-anchor the rules sheet
 * and every confirm dialog it ever opens. It costs nothing to take off, and
 * leaving it on is the kind of thing that surfaces a week later as a modal in
 * the wrong place.
 *
 * **The incoming screen is positioned before it is painted.** Nothing covers the
 * screen during a slide, so the new route is visible the instant it mounts — and
 * this runs as a LAYOUT effect, in the same commit as the route swap (see the
 * store, which fires both in one callback), which is what stops a frame of it
 * sitting at rest in the middle of the movement.
 */
function useSlideAnimation(
  direction: SlideDirection | null,
  phase: "covering" | "revealing",
  runId: number,
  plan: { coverMs: number; revealMs: number } | null,
): void {
  useLayoutEffect(() => {
    if (!direction || !plan) return;
    const stage = document.querySelector<HTMLElement>(".tx-stage");
    if (!stage) return;

    const stroke = phase === "covering" ? outStroke(direction) : inStroke(direction);
    const duration = Math.max(1, phase === "covering" ? plan.coverMs : plan.revealMs);
    let current: { el: HTMLElement; animation: Animation } | null = null;

    /**
     * Puts the stroke on whatever screen is mounted right now, picking up where
     * the last one had got to.
     *
     * The resume is what makes re-applying safe rather than a stutter: the
     * incoming route is code-split, so React renders Suspense's fallback first
     * and swaps the real screen in a beat later. The first version animated
     * whatever was there at the instant of the route change, which was that
     * fallback — and the transform went out of the document with it, so the new
     * screen simply appeared in place with the whole entrance skipped.
     */
    const applyTo = (el: HTMLElement) => {
      const resumeAt = current ? Number(current.animation.currentTime ?? 0) : 0;
      current?.animation.cancel();
      current?.el.style.removeProperty("transform");
      if (current) releaseChrome(current.el);
      for (const a of el.getAnimations()) a.cancel();
      holdScrollbar(stage.parentElement, el);
      // Measured BEFORE the stroke goes on: that is the moment the chrome
      // re-anchors, and the measurement is what pins it back where it was.
      const before = phase === "covering" ? chromeRects(el) : null;
      el.setAttribute("data-tx-sliding", phase === "covering" ? "out" : "in");
      const animation = el.animate(stroke.keyframes, { duration, easing: stroke.easing, fill: "both" });
      animation.currentTime = Math.min(resumeAt, duration);
      if (before) pinChrome(el, before, resumeAt > 0);
      current = { el, animation };
    };

    const child = stage.firstElementChild;
    if (child instanceof HTMLElement) applyTo(child);

    // `.tx-stage` generates no box, but it is still a node with children, so the
    // swap is observable here and nowhere more convenient.
    const watch = new MutationObserver(() => {
      const next = stage.firstElementChild;
      if (next instanceof HTMLElement && next !== current?.el) applyTo(next);
    });
    watch.observe(stage, { childList: true });

    return () => {
      watch.disconnect();
      current?.animation.cancel();
      // Cleared rather than left filled: a transformed element is the containing
      // block for any `position: fixed` inside it, so a screen still holding its
      // transform would quietly re-anchor the rules sheet and every confirm
      // dialog it ever opens.
      current?.el.style.removeProperty("transform");
      stage.parentElement?.style.removeProperty("overflow-y");
      if (current) {
        releaseChrome(current.el);
        // The screen that slid IN is at rest and back in the tray's frame, so its
        // chrome is where it belongs again and can come up. An outgoing screen is
        // on its way out of the document and gets nothing.
        if (phase === "revealing") fadeChromeIn(current.el);
      }
    };
  }, [runId, phase, direction, plan?.coverMs, plan?.revealMs]);
}

/**
 * Corner chrome is anything a screen pins with `position: fixed` (nothing does
 * today: the home screen's account pill, the last user, now sits in its flow),
 * and the stroke puts a transform on its screen. A transformed element is the
 * containing block for anything fixed inside it, so the moment a slide starts
 * that chrome re-anchors from the tray to the screen's own box and visibly hops,
 * and it hops back the moment the stroke is cleared. Marking an element
 * `data-tx-chrome` takes it out of the movement: it fades out as the screen
 * starts to go, and fades in once the screen it arrived on has come to rest.
 *
 * Out is quicker than in, on purpose. Leaving, it only has to be gone before the
 * screen breaks free (`STICTION_BREAK_AT` of the stroke); arriving, it is the
 * last thing to settle and a slower fade reads as the screen finishing rather
 * than as a pop.
 */
const CHROME_FADE_OUT_MS = 70;

/**
 * Settles the tray's scrollbar for the length of a stroke, from the screen's
 * RESTING height.
 *
 * A transform counts towards scrollable overflow, so a screen sliding down out
 * of a tray that had no scrollbar gives it one a few pixels into the stroke —
 * and on a desktop that bar takes width, so the whole screen narrows and
 * everything on it shifts sideways as it starts to move (and back again as a
 * screen arriving from below comes to rest). `offsetHeight` ignores transforms,
 * so it answers what the screen needs at rest, and the tray is held to that.
 */
function holdScrollbar(scroller: HTMLElement | null, screen: HTMLElement): void {
  if (!scroller) return;
  scroller.style.setProperty("overflow-y", screen.offsetHeight > scroller.clientHeight ? "scroll" : "hidden");
}
const CHROME_FADE_IN_MS = 260;

function chromeOf(el: HTMLElement): HTMLElement[] {
  return Array.from(el.querySelectorAll<HTMLElement>("[data-tx-chrome]"));
}

function chromeRects(el: HTMLElement): Map<HTMLElement, DOMRect> {
  return new Map(chromeOf(el).map((c) => [c, c.getBoundingClientRect()]));
}

/**
 * Holds the outgoing screen's chrome where it was drawn before the stroke went
 * on, for as long as it takes to fade.
 *
 * `translate`, the individual property, rather than `transform`: a button's
 * pressed state is a transform, and the chrome being tapped is exactly what
 * started this navigation. The screen barely moves during the stiction lead-in,
 * which is the whole window the fade runs in, so one offset holds for all of it.
 */
function pinChrome(el: HTMLElement, before: Map<HTMLElement, DOMRect>, alreadyGone: boolean): void {
  for (const [c, was] of before) {
    const now = c.getBoundingClientRect();
    c.style.setProperty("translate", `${was.left - now.left}px ${was.top - now.top}px`);
    // From whatever it rests at, not from 1: chrome may be dimmed by design,
    // and a fade that started at full strength would flash it first.
    // Held at 0 by its own fill rather than by the stylesheet's hide, which is
    // for the INCOMING screen only — under that rule the "whatever it rests at"
    // this fade starts from would already be 0.
    c.animate({ opacity: 0 }, { duration: alreadyGone ? 1 : CHROME_FADE_OUT_MS, easing: "ease-out", fill: "forwards" });
  }
}

/** Takes off everything a stroke put on the chrome, including the hide. */
function releaseChrome(el: HTMLElement): void {
  el.removeAttribute("data-tx-sliding");
  for (const c of chromeOf(el)) {
    for (const a of c.getAnimations()) a.cancel();
    c.style.removeProperty("translate");
  }
}

function fadeChromeIn(el: HTMLElement): void {
  for (const c of chromeOf(el)) {
    // One keyframe at the start: the end is implicit, i.e. its own resting opacity.
    c.animate([{ opacity: 0, offset: 0 }], { duration: CHROME_FADE_IN_MS, easing: "ease-out" });
  }
}

/**
 * The sounds a transition makes.
 *
 * Deliberately spare and quiet. This fires on every navigation, which makes it
 * the most-heard sound in the app by a wide margin — the bar for it is not "does
 * this sound good once" but "is this still bearable on the hundredth menu tap".
 * Each one is a single existing table sound at its normal level, no more.
 *
 * `sound` owns whether anything is audible at all (mute, the sfx setting, the
 * browser's autoplay gate), so none of that is asked here.
 */
function useTransitionSound(runId: number, style: string, phase: string, revealMs: number, coverMs: number): void {
  useEffect(() => {
    if (!runId || style === "fade") return;
    if (phase === "covering") {
      if (style === "slide") {
        // At the BREAK, not at the tap: the sound is friction letting go, and
        // for the first sixth of the stroke nothing is really moving.
        const at = window.setTimeout(() => sound.cardSlide(), Math.round(coverMs * STICTION_BREAK_AT));
        return () => window.clearTimeout(at);
      }
      if (style === "cut") {
        sound.cardSlide();
        return;
      }
      // Four dealers, four volleys: one tick per corner rather than one per
      // card, which at twenty-odd cards would be a rattle.
      const ticks = [0, 90, 180, 270].map((at) => window.setTimeout(() => sound.cardSlide(), at));
      return () => ticks.forEach((t) => window.clearTimeout(t));
    }
    if (style === "slide") {
      // The screen coming to rest against friction, at the end rather than the
      // start: it arrives already moving and what there is to hear is it
      // stopping.
      const at = window.setTimeout(() => sound.cardSlide(), Math.max(0, revealMs - 170));
      return () => window.clearTimeout(at);
    }
    if (style === "deal") {
      sound.railSlide();
      // The rail reaching the far side, which is also the last card leaving.
      const thud = window.setTimeout(() => sound.lockThud(), Math.max(0, revealMs - 90));
      return () => window.clearTimeout(thud);
    }
    sound.packSweep();
  }, [runId, style, phase, revealMs, coverMs]);
}
